// src/store/modules/userStore.js
import { defineStore } from 'pinia';
import { getUserInfo } from '@/api/user';
import { checkAdmin } from '@/api/admin';
import axios from 'axios';
import {ElMessage} from "element-plus";
import request from '@/utils/request'
import { batchAddFavorite, getFavoriteList } from '@/api/favorite';
// eslint-disable-next-line no-unused-vars
import {getPlatformFieldMap} from "@/config/tabConfig";
import favoriteManager from '@/utils/favoriteManager';

// 模块级刷新 Promise 队列，用于防止并发刷新导致 token 被覆盖
let tokenRefreshPromise = null;

// 定义用户全局状态仓库
export const useUserStore = defineStore('user', {
  // 全局状态数据
  state: () => ({
    isLogin: false, // 全局登录状态
    isAdmin: false, // 是否管理员
    userInfo: {     // 全局用户信息
      avatar: 'https://picsum.photos/seed/avatar/100/100',
      nickname: '未登录',
      phone: '',
      userId: '',
      // 会员相关字段
      level: 0,
      title: '稚子蒙学',
      points: 0,
      needGuideBindPlatform: false,
      guideMessage: ''
    },
    // 签到状态
    signStatus: {
      signedToday: false,
      continuousDays: 0,
      nextExtraRewardDays: 7,
      weekSignDates: []
    },
    accessToken: localStorage.getItem('accessToken') || '', // 访问令牌
    refreshToken: localStorage.getItem('refreshToken') || '',  // 刷新令牌
    favoriteCachePrimed: false
  }),

  // 派生状态（可选，简化模板使用）
  getters: {
    // 快速获取用户是否登录（refreshToken 或 accessToken 任一存在即视为登录态）
    hasLogin: (state) => state.isLogin && (!!state.refreshToken || !!state.accessToken),
  },

  // 同步/异步方法（修改状态的唯一入口）
  actions: {
    /**
     * 初始化用户登录状态（所有组件均可调用）
     */
    async initUserStatus() {
      try {
        // 1. 从本地存储读取基础数据
        const refreshTokenStr = localStorage.getItem('refreshToken');
        const accessTokenStr = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        const userInfoStr = localStorage.getItem('userInfo') || '{}';
        let userInfoLocal = {};
        try {
          userInfoLocal = JSON.parse(userInfoStr);
        } catch (e) {
          userInfoLocal = {};
        }

        // 2. 更新仓库基础状态
        this.refreshToken = refreshTokenStr || '';
        this.accessToken = accessTokenStr || '';
        if (this.accessToken) {
          localStorage.setItem('accessToken', this.accessToken);
          sessionStorage.setItem('accessToken', this.accessToken);
        }
        this.userInfo = {...this.userInfo, ...userInfoLocal};

        // 3. 正确判断登录状态（refreshToken 或 accessToken 任一存在即可）
        const hasToken = !!String(this.refreshToken || '').trim() || !!String(this.accessToken || '').trim();
        this.isLogin = hasToken;
        
        // console.log('【用户仓库】initUserStatus调试:', { hasToken, isLogin: this.isLogin, hasRefreshToken: !!this.refreshToken, refreshTokenLength: (this.refreshToken || '').length, hasAccessToken: !!this.accessToken, accessTokenLength: (this.accessToken || '').length });

        // 4. 处理异常状态：只有accessToken没有refreshToken（状态损坏），清除让用户重新登录
        if (this.isLogin && !this.refreshToken && this.accessToken) {
          console.warn('【用户仓库】检测到异常状态：有accessToken但无refreshToken，清除状态让用户重新登录');
          this.resetUserStatus();
          return;
        }

        // 5. 若已登录，尝试刷新accessToken并获取最新用户信息
        // 使用 Promise 队列防止并发调用导致token被覆盖
        if (this.isLogin && this.refreshToken) {
          // 如果已经有刷新请求在进行，等待它完成
          if (tokenRefreshPromise) {
            // console.log('【用户仓库】正在刷新Token中，等待...');
            try {
              await tokenRefreshPromise;
              // console.log('【用户仓库】等待Token刷新完成');
              // 等待完成后直接获取用户信息，不要再次刷新
              if (this.isLogin) {
                const res = await getUserInfo();
                if (res.code === 0) {
                  const newUserInfo = {
                    avatar: res.data.avatar || this.userInfo.avatar,
                    nickname: res.data.username || this.userInfo.nickname,
                    phone: res.data.phone || this.userInfo.phone,
                    userId: res.data.id || this.userInfo.userId,
                    level: res.data.level || 0,
                    title: res.data.title || '稚子蒙学',
                    points: res.data.points || 0,
                    needGuideBindPlatform: res.data.needGuideBindPlatform || false,
                    guideMessage: res.data.guideMessage || ''
                  };
                  this.userInfo = newUserInfo;
                  localStorage.setItem('userInfo', JSON.stringify({...userInfoLocal, ...newUserInfo}));
                  this.fetchSignStatus();
                }
              }
              return;
            } catch (e) {
              // console.log('【用户仓库】等待刷新失败，将自己发起刷新', e);
              // 等待失败，继续执行自己的刷新
              tokenRefreshPromise = null;
            }
          }
          
          // 发起新的刷新并记录 Promise
          // console.log('【用户仓库】有refreshToken，尝试刷新...');
          tokenRefreshPromise = this._doRefreshToken()
            .finally(() => {
              tokenRefreshPromise = null; // 完成后清空
            });
          
          await tokenRefreshPromise;
        }

        // 5. 获取最新用户信息，更新全局状态与本地存储
        if (this.isLogin) {
          try {
            const res = await getUserInfo();
            if (res.code === 0) {
              const newUserInfo = {
                avatar: res.data.avatar || this.userInfo.avatar,
                nickname: res.data.username || this.userInfo.nickname,
                phone: res.data.phone || this.userInfo.phone,
                userId: res.data.id || this.userInfo.userId,
                // 会员相关字段
                level: res.data.level || 0,
                title: res.data.title || '稚子蒙学',
                points: res.data.points || 0,
                needGuideBindPlatform: res.data.needGuideBindPlatform || false,
                guideMessage: res.data.guideMessage || ''
              };
              this.userInfo = newUserInfo;
              // 更新本地存储，保持一致性
              localStorage.setItem('userInfo', JSON.stringify({...userInfoLocal, ...newUserInfo}));

              // 获取签到状态
              this.fetchSignStatus();
            } else {
              // getUserInfo返回非0 code，可能是token失效
              console.warn('【用户仓库】获取用户信息失败，code:', res.code);
            }
          } catch (err) {
            // 可能是401 token失效，清除登录状态
            console.warn('【用户仓库】获取用户信息异常:', err);
            // 不再此处清除状态，让拦截器的refreshAccessToken处理
          }

          // 检查管理员身份
          this.checkAdminStatus();

          // 登录后首次加载时预热收藏缓存，确保刷新后回显
          if (!this.favoriteCachePrimed) {
            try {
              const res = await getFavoriteList({});
              const list = Array.isArray(res.data) ? res.data : [];
              list.forEach(item => {
                const skuId = String(item.sku_id || item.skuId || '').trim();
                if (!skuId) return;
                const platform = item.platform || 'jd';
                favoriteManager.updateCache(skuId, platform, true);
              });
              this.favoriteCachePrimed = true;
              window.dispatchEvent(new CustomEvent('favoriteSynced'));
            } catch (e) {
              console.warn('【用户仓库】预热收藏缓存失败:', e);
            }
          }
        } else {
          // 未登录时不清空 token，避免刷新误登出
          this.isLogin = false;
        }
      } catch (err) {
        console.error('【用户仓库】初始化用户状态失败:', err);
        this.resetUserStatus();
      }
    },

    /**
     * 登录成功后更新全局状态
     * @param {Object} loginRes - 登录接口返回数据（包含tokens与用户信息）
     */
    setUserLogin(loginRes) {
      const {accessToken, refreshToken, user} = loginRes;
      const validAccessToken = typeof accessToken === 'object' && accessToken.accessToken
        ? accessToken.accessToken
        : accessToken;
      // 1. 更新仓库状态
      this.accessToken = validAccessToken;
      this.refreshToken = refreshToken;
      this.isLogin = true;
      this.userInfo = {
        ...this.userInfo,
        nickname: user.username,
        phone: user.phone || '',
        userId: user.id,
        avatar: user.avatar || this.userInfo.avatar,
        isLogin: true
      };

      // 2. 同步到本地存储
      localStorage.setItem('accessToken', this.accessToken);
      sessionStorage.setItem('accessToken', this.accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(this.userInfo));

      // 登录成功后，同步游客收藏到用户账号
      // 使用 favoriteManager.getGuestId() 获取设备指纹（不依赖localStorage）
      const guestId = favoriteManager.getGuestId();
      if (guestId) {
        console.log('[userStore] login success, syncing guest favorites for:', guestId);
        this.syncGuestFavorites(guestId);
      }
    },

    /**
     * 检查是否为管理员
     */
    async checkAdminStatus() {
      if (!this.isLogin) {
        this.isAdmin = false;
        return false;
      }
      try {
        const res = await checkAdmin();
        if (res.code === 200 || res.code === 0) {
          this.isAdmin = res.data?.isAdmin === true;
        } else {
          this.isAdmin = false;
        }
      } catch (e) {
        this.isAdmin = false;
      }
      return this.isAdmin;
    },

    /**
     * 获取签到状态
     */
    async fetchSignStatus() {
      if (!this.isLogin) return;
      try {
        const { getSignStatus } = await import('@/api/user');
        const res = await getSignStatus();
        if (res.code === 0) {
          this.signStatus = {
            signedToday: res.data.signedToday || false,
            continuousDays: res.data.continuousDays || 0,
            nextExtraRewardDays: res.data.nextExtraRewardDays || 7,
            isSunday: res.data.isSunday || false,
            weekSignDates: res.data.weekSignDates || [],
            remainRepairSign: res.data.remainRepairSign || 0
          };
        }
      } catch (err) {
        console.warn('【用户仓库】获取签到状态失败:', err);
      }
    },

    /**
     * 执行签到
     * @returns {object} 签到结果
     */
    async doSign() {
      if (!this.isLogin) {
        return { success: false, msg: '请先登录' };
      }
      try {
        const { sign } = await import('@/api/user');
        const res = await sign();
        if (res.code === 0) {
          // 更新积分和等级
          this.userInfo.points = res.data.newPoints;
          this.userInfo.level = res.data.newLevel;
          this.userInfo.title = res.data.newTitle;
          // 更新签到状态
          this.signStatus.signedToday = true;
          this.signStatus.continuousDays = res.data.continuousDays;
          // 更新本周签到日期列表（使用本地日期格式）
          const formatDateStr = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          const today = formatDateStr(new Date());
          if (!this.signStatus.weekSignDates.includes(today)) {
            this.signStatus.weekSignDates.push(today);
          }
          // 更新本地存储
          localStorage.setItem('userInfo', JSON.stringify({
            ...JSON.parse(localStorage.getItem('userInfo') || '{}'),
            points: res.data.newPoints,
            level: res.data.newLevel,
            title: res.data.newTitle
          }));
          return res.data;
        } else {
          return { success: false, msg: res.msg };
        }
      } catch (err) {
        console.error('【用户仓库】签到失败:', err);
        return { success: false, msg: '签到失败，请稍后重试' };
      }
    },

    /**
     * 退出登录，重置所有状态
     */
    resetUserStatus() {
      // 1. 重置仓库状态
      this.isLogin = false;
      this.userInfo = {
        avatar: 'https://picsum.photos/seed/avatar/100/100',
        nickname: '未登录',
        phone: '',
        userId: ''
      };
      this.accessToken = '';
      this.refreshToken = '';

      // 2. 清空本地存储
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      sessionStorage.removeItem('accessToken');
    },

    /**
     * 执行 Token 刷新（供 Promise 队列调用）
     */
    async _doRefreshToken() {
      let retryCount = 0;
      const maxRetries = 2;
      
      const attemptRefresh = async () => {
        // eslint-disable-next-line no-useless-catch
        try {
          const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '';
          const res = await axios.post(
            `${API_BASE_URL}/users/refreshToken`,
            { refreshToken: this.refreshToken },
            { headers: { 'Content-Type': 'application/json;charset=utf-8' } },
            { timeout: 5000 } // 5秒超时
          );
          
          // 检查返回码，确保刷新成功
          if (res.data.code !== 0 && res.data.code !== 200) {
            console.warn('【用户仓库】刷新Token返回错误码:', res.data.code, res.data.msg);
            // 只有明确知道refreshToken无效时才清除登录状态
            if (res.data.code === 401 || res.data.msg?.includes('不存在') || res.data.msg?.includes('无效') || res.data.msg?.includes('失效')) {
              console.warn('【用户仓库】refreshToken已失效，清除登录状态');
              this.resetUserStatus();
            } else {
              console.warn('【用户仓库】刷新Token失败但token可能有效，保留状态');
            }
            return { success: false, isTokenInvalid: res.data.code === 401 };
          }
          
          let newAccessToken = res.data.data.accessToken;
          let newRefreshToken = res.data.data.refreshToken;
          if (typeof newAccessToken === 'object' && newAccessToken.accessToken) {
            newAccessToken = newAccessToken.accessToken;
          }
          if (typeof newRefreshToken === 'object' && newRefreshToken.refreshToken) {
            newRefreshToken = newRefreshToken.refreshToken;
          }
          // 更新 accessToken
          this.accessToken = newAccessToken;
          localStorage.setItem('accessToken', this.accessToken);
          sessionStorage.setItem('accessToken', this.accessToken);
          // 更新 refreshToken（后端每次刷新会返回新的refreshToken）
          if (newRefreshToken) {
            this.refreshToken = newRefreshToken;
            localStorage.setItem('refreshToken', this.refreshToken);
          }
          // console.log('【用户仓库】Token刷新成功');
          return { success: true };
        } catch (refreshErr) {
          throw refreshErr; // 向上抛出，供重试逻辑处理
        }
      };
      
      // 重试逻辑
      while (retryCount < maxRetries) {
        // eslint-disable-next-line no-useless-catch
        try {
          await attemptRefresh();
          return; // 成功或明确业务错误，直接返回
        } catch (refreshErr) {
          retryCount++;
          console.warn(`【用户仓库】刷新Token失败（第${retryCount}次）:`, refreshErr.message || refreshErr);
          
          // 判断是否是网络/连接错误，需要重试
          const isNetworkError = 
            !refreshErr.response || // 网络错误（后端不可达）
            refreshErr.code === 'ECONNRESET' || // 连接被重置
            refreshErr.code === 'ETIMEDOUT' || // 连接超时
            refreshErr.message?.includes('timeout') || // 超时
            refreshErr.message?.includes('Network Error'); // 网络错误
          
          if (isNetworkError && retryCount < maxRetries) {
            console.warn(`【用户仓库】网络错误，${retryCount * 1000}ms后重试...`);
            await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
            continue;
          }
          
          // 最后一次失败或明确业务错误，不清除登录状态
          console.warn('【用户仓库】刷新Token失败但保留登录状态（网络可能临时不可用）');
          return;
        }
      }
    },

    async handleLogout() {
      try {
        if (this.accessToken) {
          // 关键：直接用request.post，只传2个参数（接口路径、请求配置），不传请求体
          // 注意：axios/request的post方法，若不传请求体，可直接省略第二个参数，或传undefined
          await request.post(
            '/users/logout', // 第1个参数：接口路径
            { // 第2个参数：请求配置（直接在这里写headers，不传递请求体）
              headers: {
                Authorization: `Bearer ${this.accessToken}` // 正确携带请求头
              }
            }
          );
        }
      } catch (error) {
        // 此时如果还报500，大概率是其他问题，但先捕获，不阻断前端重置
        console.error('【用户仓库】调用后端退出接口失败:', error);
        // ElMessage.warning('后端令牌注销异常，已为你清空本地登录状态');
                    window.dispatchEvent(new CustomEvent('favoriteSynced'));
      } finally {
        // 无论如何，都重置前端状态（保证用户体验）
        this.resetUserStatus();
        ElMessage.success('退出登录成功');
      }
    },

    // 同步本地收藏到后端
    async syncLocalFavorite() {
      try {
        const favList = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
        const productCache = JSON.parse(localStorage.getItem('productCache') || '{}');
        if (favList.length === 0) return;

        // 过滤有效数据
        const syncData = favList.filter(item => {
          const skuId = String(item.skuId).trim();
          const platform = String(item.platform).trim();
          return skuId && skuId.length > 3 && platform;
        }).map(item => {
          const skuIdStr = String(item.skuId).trim();
          const platform = String(item.platform).trim();
          const localProduct = productCache[skuIdStr] || {};

          return {
            skuId: skuIdStr,
            platform: platform,
            goods_name: localProduct.goods_name || `商品${skuIdStr}`,
            goods_img: localProduct.goods_img || '',
            price: localProduct.price || '0.00',
            original_price: localProduct.original_price || '0.00',
            sales_count: localProduct.sales_count || 0
          };
        });

        if (syncData.length === 0) {
          ElMessage.warning('无有效本地收藏数据可同步');
          return;
        }

        // 调用批量收藏接口
        const res = await batchAddFavorite({ syncData });
        if (res?.code === 200) {
          // 🔥 核心修复：同步成功后更新全局缓存
          window.dispatchEvent(new CustomEvent('favoriteSynced'));
          syncData.forEach(item => {
            favoriteManager.updateCache(item.skuId, item.platform, true);
          });

          // 清空本地缓存
          localStorage.setItem('favoriteProducts', JSON.stringify([]));
          localStorage.setItem('productCache', JSON.stringify({}));

          ElMessage.success(`成功同步 ${syncData.length} 个本地收藏到账号`);
          // window.dispatchEvent(new CustomEvent('favoriteSynced'));
        } else {
          ElMessage.warning('本地收藏同步失败，保留本地数据');
        }
      } catch (err) {
        console.error('同步本地收藏到后端失败：', err);
        ElMessage.warning('本地收藏同步失败，保留本地数据');
      }
    },

    // 同步游客收藏到用户账号
    async syncGuestFavorites(guestId) {
      if (!this.userInfo?.userId || !guestId) {
        console.log('[userStore] syncGuestFavorites: 参数不完整', { userId: this.userInfo?.userId, guestId });
        return { code: 400, msg: '参数不完整' };
      }
      
      try {
        const { syncGuestFavorites } = await import('@/api/favorite');
        const res = await syncGuestFavorites(guestId, this.userInfo.userId);
        console.log('[userStore] 同步收藏结果:', res);
        return res;
      } catch (err) {
        console.error('[userStore] syncGuestFavorites error:', err);
        return { code: 500, msg: '同步失败' };
      }
    },
  }
});
